import dayjs from 'dayjs'

/**
 * The zones admins actually look for, under the names they look for them by. A raw IANA list
 * only offers cities, so "our office is on Pacific Time" used to end at Los Angeles - harmless
 * while it kept the same clock as Vancouver, an hour off all winter since British Columbia
 * stopped changing clocks on 2026-11-01.
 */
export const COMMON_TIMEZONES = [
  {
    id: 'America/Vancouver',
    name: 'Pacific Time — British Columbia',
    keywords: 'pacific pt pst pdt bc british columbia vancouver victoria surrey burnaby kelowna'
  },
  {
    id: 'America/Los_Angeles',
    name: 'Pacific Time — US',
    keywords: 'pacific pt pst pdt usa california washington oregon nevada seattle san francisco portland'
  },
  { id: 'America/Whitehorse', name: 'Yukon', keywords: 'yukon whitehorse' },
  { id: 'America/Edmonton', name: 'Mountain Time — Alberta', keywords: 'mountain mt alberta calgary edmonton' },
  { id: 'America/Denver', name: 'Mountain Time — US', keywords: 'mountain mt usa colorado utah denver' },
  { id: 'America/Phoenix', name: 'Arizona', keywords: 'mountain mt arizona phoenix' },
  { id: 'America/Regina', name: 'Saskatchewan', keywords: 'central saskatchewan regina saskatoon' },
  { id: 'America/Winnipeg', name: 'Central Time — Manitoba', keywords: 'central ct manitoba winnipeg' },
  { id: 'America/Chicago', name: 'Central Time — US', keywords: 'central ct usa texas illinois chicago dallas houston' },
  { id: 'America/Toronto', name: 'Eastern Time — Ontario, Quebec', keywords: 'eastern et ontario quebec toronto montreal ottawa' },
  { id: 'America/New_York', name: 'Eastern Time — US', keywords: 'eastern et usa new york boston atlanta miami' },
  { id: 'America/Halifax', name: 'Atlantic Time', keywords: 'atlantic at nova scotia new brunswick halifax' },
  { id: 'America/St_Johns', name: 'Newfoundland', keywords: 'newfoundland st johns' }
]

const commonById = COMMON_TIMEZONES.reduce((index, zone) => ({ ...index, [zone.id]: zone }), {})

/** "Pacific Time — British Columbia (Vancouver, UTC-07:00)" / "Helsinki (UTC+02:00)" */
export const timezoneDisplayName = (tz) => {
  const common = commonById[tz.id]
  return common ? `${common.name} (${tz.label}, ${tz.offsetLabel})` : `${tz.label} (${tz.offsetLabel})`
}

const haystack = (tz) => {
  const common = commonById[tz.id]
  return [tz.id, tz.label, tz.group, tz.offsetLabel, common && common.name, common && common.keywords]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/_/g, ' ')
}

/**
 * Items for a searchable zone picker: the common zones first, then everything else in the order
 * the server sent it. Every word of the query has to match somewhere - id, city, region, offset,
 * or the common name and its keywords ("pacific", "bc", "alberta").
 */
export const timezoneOptions = (timezones, query = '') => {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  const matches = (tz) => {
    const text = haystack(tz)
    return words.every((word) => text.includes(word))
  }
  const toItem = (tz) => ({ id: tz.id, text: timezoneDisplayName(tz), common: Boolean(commonById[tz.id]) })

  const known = timezones.reduce((index, tz) => ({ ...index, [tz.id]: tz }), {})
  const common = COMMON_TIMEZONES.map((zone) => known[zone.id]).filter((tz) => tz && matches(tz))
  const rest = timezones.filter((tz) => !commonById[tz.id] && matches(tz))

  return [...common, ...rest].map(toItem)
}

/** A word of warning for the zones people pick by mistake; null for the rest. */
export const timezoneHint = (id) => {
  if (id === 'America/Los_Angeles') {
    return 'Offices in British Columbia need "Pacific Time — British Columbia" (Vancouver): ' +
      'since November 1, 2026 BC stays on UTC-7 all year, Los Angeles moves to UTC-8 for the winter.'
  }
  return null
}

/**
 * Zones this browser reads differently from the server - its time zone data is outdated, so it
 * shows (and books) times in those zones an hour or so off. Compared now and half a year ahead,
 * the two moments the server reports, so a rule change that has not kicked in yet still shows.
 *
 * @param timezones the server's list: [{ id, offset, offsetLater }], offsets in seconds
 * @param zoneIds   the zones that matter here (the buildings'), duplicates allowed
 * @return [{ id, browser, server }] offsets in seconds, one per stale zone
 */
export const staleTimezones = (timezones, zoneIds, now = dayjs()) => {
  const later = now.add(182, 'day')
  const byId = timezones.reduce((index, tz) => ({ ...index, [tz.id]: tz }), {})
  const result = []

  for (const id of new Set(zoneIds.filter(Boolean))) {
    const server = byId[id]
    if (!server) continue
    for (const [moment, expected] of [[now, server.offset], [later, server.offsetLater]]) {
      if (typeof expected !== 'number') continue
      let browser
      try {
        browser = moment.tz(id).utcOffset() * 60
      } catch (e) {
        continue // a zone this browser does not know at all is not something an update note fixes
      }
      if (browser !== expected) {
        result.push({ id, browser, server: expected })
        break
      }
    }
  }
  return result
}
